import type { Rng } from '../rng';

/**
 * Bruit de valeur 2D seedé (réseau de valeurs aléatoires, interpolation lissée), deux octaves.
 * `scale` : taille d'une maille de l'octave principale, dans l'unité des coordonnées passées (ici des cellules).
 * Valeurs dans [0, 1]. Tout l'aléatoire vient du `Rng` fourni (tirages faits à la création).
 */
export function createNoise(rng: Rng, scale: number, width = 40, height = 40): (x: number, y: number) => number {
  const octaves = [{ s: scale, w: 0.7 }, { s: scale / 2.3, w: 0.3 }].map(({ s, w }) => {
    const nx = Math.ceil(width / s) + 2, ny = Math.ceil(height / s) + 2;
    const v = new Float32Array(nx * ny);
    for (let i = 0; i < v.length; i++) v[i] = rng.next();
    return { s, w, nx, ny, v };
  });
  const smooth = (t: number): number => t * t * (3 - 2 * t);
  return (x, y) => {
    let sum = 0;
    for (const o of octaves) {
      const fx = Math.max(0, x / o.s), fy = Math.max(0, y / o.s);
      const x0 = Math.min(Math.floor(fx), o.nx - 2), y0 = Math.min(Math.floor(fy), o.ny - 2);
      const tx = smooth(Math.min(1, fx - x0)), ty = smooth(Math.min(1, fy - y0));
      const a = o.v[y0 * o.nx + x0]!, b = o.v[y0 * o.nx + x0 + 1]!, c = o.v[(y0 + 1) * o.nx + x0]!, d = o.v[(y0 + 1) * o.nx + x0 + 1]!;
      sum += o.w * ((a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty);
    }
    return sum;
  };
}
